import { useCallback, useEffect, useRef, useState } from 'react';

export type UseWorkspaceAgentPreviewCardDisclosureOptions = {
  /**
   * Where focus lands when the card closes for a navigation. A composer chip hands it back to the editor;
   * a history chip has no editor to return to and passes its own trigger instead.
   */
  focusNavigationOrigin: () => void;
  /**
   * Whether the chip still has a subject to draw a card for. A chip that drops back to its pending or
   * unavailable form stops rendering the popover, and an open flag left behind would reopen the card
   * on its own once the subject returned.
   */
  hasSubject: boolean;
  /**
   * Composer only: any close the user did not aim elsewhere hands the caret back to the editor with the
   * chip selected, because the chip sits in a `contentEditable={false}` island a keyboard user cannot
   * escape. Wired through `restoreFocusAfterClose`, which is the only caller.
   */
  onCardClose?: () => void;
  /** Fires once when Radix opens the preview card (`false` → `true`), not on hover-only surfaces. */
  onCardOpen: () => void;
};

/**
 * Preview-card disclosure for both chip surfaces. `onCardClose` runs from exactly one place — Radix's
 * close autofocus, the only hook every close reaches — and the flag below is how a close that already
 * chose a destination (an outside press, a Tab off the card, a navigation) opts out of it.
 * `handleOpenChange` only mirrors Radix's own state: it never sees the closes the caller makes itself.
 */
export const useWorkspaceAgentPreviewCardDisclosure = ({
  focusNavigationOrigin,
  hasSubject,
  onCardClose,
  onCardOpen,
}: UseWorkspaceAgentPreviewCardDisclosureOptions) => {
  const [isCardOpen, setIsCardOpen] = useState(false);
  const [isNavigatingAway, setIsNavigatingAway] = useState(false);
  /**
   * Set by a close that already chose where focus goes; unset means restore. Declared without an
   * initializer — `useRef<boolean>(false)` reads to Biome as the literal `false` for the whole module,
   * and `undefined` is falsy, so the default is the same "restore" it always was.
   */
  const shouldSuppressEditorFocusRestoreRef = useRef<boolean>();

  // The card has nothing left to draw, so it goes with the subject rather than waiting for a dismissal.
  useEffect(() => {
    if (!hasSubject && isCardOpen) {
      setIsCardOpen(false);
    }
  }, [hasSubject, isCardOpen]);

  /**
   * Closes for a navigation. Focus has to move here, and before `navigate`: the asset modal reads the
   * active element as it mounts and returns focus there when it closes, so a caller that navigated first
   * would hand it `<body>`. Radix's close autofocus still fires for this close, a tick after the layer
   * goes — hence the flag, or that restore would pull focus back out of whatever just opened.
   */
  const navigateFromCard = useCallback(
    (navigate: () => void) => {
      shouldSuppressEditorFocusRestoreRef.current = true;
      setIsNavigatingAway(true);
      setIsCardOpen(false);
      focusNavigationOrigin();
      navigate();
    },
    [focusNavigationOrigin],
  );

  /**
   * The dismissal the caller owns: the card's own close button. Re-arms rather than reading the flag: an
   * outside press Radix went on to cancel leaves it off with no close of its own to reset it.
   */
  const closeCard = useCallback(() => {
    shouldSuppressEditorFocusRestoreRef.current = false;
    setIsCardOpen(false);
  }, []);

  /**
   * Radix dispatches both outside events before the close they cause, so the flag is set by the time that
   * runs. Pulling the caret into the composer afterwards would yank focus from wherever the press was aimed.
   */
  const suppressEditorFocusRestore = useCallback(() => {
    shouldSuppressEditorFocusRestoreRef.current = true;
  }, []);

  /**
   * Composer only, from Radix's close autofocus. It runs after the layer has gone, so the focus it moves
   * is no longer an outside focus the still-mounted layer reports straight back as an aimed dismissal —
   * which is what waived the restore for the following close while it ran from the open change.
   */
  const restoreFocusAfterClose = useCallback(() => {
    const shouldSuppressEditorFocusRestore = shouldSuppressEditorFocusRestoreRef.current;

    // Armed for the next dismissal, whichever kind it turns out to be.
    shouldSuppressEditorFocusRestoreRef.current = false;

    if (!shouldSuppressEditorFocusRestore) {
      onCardClose?.();
    }
  }, [onCardClose]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen && !isCardOpen) {
        onCardOpen();
      }

      setIsCardOpen(isOpen);

      if (isOpen) {
        setIsNavigatingAway(false);
      }
    },
    [isCardOpen, onCardOpen],
  );

  return {
    closeCard,
    handleOpenChange,
    isCardOpen,
    isNavigatingAway,
    navigateFromCard,
    restoreFocusAfterClose,
    suppressEditorFocusRestore,
  };
};
